import { createTheme } from '@mui/material/styles';
import colors from '../designTokens/colors.esm';
import colorsCustom from '../designTokens/colorsCustom';
import other from '../designTokens/other.esm';
import typeTokensModule from '../designTokens/type.esm';
import { px as breakpointPx } from '../designTokens/breakpoints';

const { paletteMap = {} } = colors;
const scaleKeys = ["50", "100", "200", "300", "400", "500", "600", "700", "800", "900"];

const getColor = (scaleName, tone) => {
  const scale = paletteMap[scaleName];
  if (scale && tone in scale) {
    return scale[tone];
  }
  const fallbackScale = colors[scaleName];
  if (Array.isArray(fallbackScale)) {
    const index = scaleKeys.indexOf(tone);
    if (index !== -1) {
      return fallbackScale[index];
    }
  }
  return undefined;
};

const typeTokens = typeTokensModule?.default || typeTokensModule;
const { fontFamilies = {}, headings = {}, display = {} } = typeTokens;
const headingSizes = headings.sizes || {};
const displaySizes = display.sizes || {};

const muiTheme = createTheme({
  palette: {
    // === Primary Color (기존 유지하되 완성) ===
    primary: {
      main: colorsCustom.ui.dark.bold,      // #1A252F
      light: colorsCustom.ui.dark.moderate, // #7A8591  
      dark: getColor('inkGray', '900'),              // #1A252F (더 어두운 버전)
      contrastText: getColor('inkGray', '50'),      // #F8FBFF (흰색 계열)
    },

    // === Secondary Color (완성) ===
    secondary: {
      main: getColor('gray', '900'),        // #030405
      light: getColor('gray', '600'),       // #596A73
      dark: getColor('gray', '900'),        // #030405
      contrastText: getColor('gray', '50'), // #FCFDFE
    },

    // === Error Color (새로 추가) ===
    error: {
      main: getColor('red', '500'),         // #d91f11ff
      light: getColor('red', '300'),        // #fc9086ff
      dark: getColor('red', '700'),         // #75160cff
      contrastText: getColor('red', '50'), // #fcf3f2ff
    },

    // === Warning Color (새로 추가) ===
    warning: {
      main: getColor('orange', '400'),      // #e86427ff
      light: getColor('orange', '200'),     // #fcbc97ff
      dark: getColor('orange', '600'),      // #8f3415ff
      contrastText: getColor('orange', '50'), // #fcf2ebff
    },

    // === Info Color (새로 추가) ===
    info: {
      main: getColor('blue', '400'),        // #144FFF
      light: getColor('blue', '200'),       // #7AA7FF
      dark: getColor('blue', '600'),        // #001CBE
      contrastText: getColor('blue', '50'), // #E4EEFF
    },

    // === Success Color (새로 추가) ===
    success: {
      main: getColor('turquoise', '400'),   // #159ab2ff
      light: getColor('turquoise', '200'),  // #81d8e5ff
      dark: getColor('turquoise', '600'),   // #09596bff
      contrastText: getColor('turquoise', '50'), // #ebf5f4ff
    },

    // === Background Colors (기존 유지) ===
    background: {
      default: colorsCustom.background, // #EFF5FC
      paper: colorsCustom.paper,        // #F8FBFF
    },

    // === Text Colors (완성) ===
    text: {
      primary: colorsCustom.ui.dark.bold,     // #1A252F
      secondary: colorsCustom.ui.dark.moderate, // #7A8591
      disabled: colorsCustom.ui.dark.subtle,  // #B8C0C8
      // 커스텀 키 유지
      light: colorsCustom.ui.dark.subtle,     // #B8C0C8
    },

    // === Divider Color (새로 추가) ===
    divider: getColor('inkGray', '300'), // #D1D6DB

    // === Action Colors (새로 추가) ===
    action: {
      active: getColor('inkGray', '600'),           // #7A8591
      hover: getColor('inkGray', '200'),            // #E4E7EA
      selected: getColor('inkGray', '300'),         // #D1D6DB
      disabled: getColor('inkGray', '400'),         // #B8C0C8
      disabledBackground: getColor('inkGray', '200'), // #E4E7EA
      focus: getColor('inkGray', '300'),            // #D1D6DB
    },
  },
  typography: {
    fontFamily: fontFamilies.ui || '"SUIT Variable", sans-serif',
    h1: {
      fontSize: headingSizes.h1?.fontSize || '2.6rem',
      fontWeight: headingSizes.h1?.fontWeight || 900,
      lineHeight: headingSizes.h1?.lineHeight || 1.2,
      letterSpacing: headingSizes.h1?.letterSpacing || '-0.02em',
      wordSpacing: headingSizes.h1?.wordSpacing || '0.02em',
    },
    h2: {
      fontSize: headingSizes.h2?.fontSize || '2.1rem',
      fontWeight: headingSizes.h2?.fontWeight || 800,
      lineHeight: headingSizes.h2?.lineHeight || 1.22,
      letterSpacing: headingSizes.h2?.letterSpacing || '-0.015em',
      wordSpacing: headingSizes.h2?.wordSpacing || '0.015em',
    },
    h3: {
      fontSize: headingSizes.h3?.fontSize || '1.6rem',
      fontWeight: headingSizes.h3?.fontWeight || 800,
      lineHeight: headingSizes.h3?.lineHeight || 1.26,
      letterSpacing: headingSizes.h3?.letterSpacing || '-0.01em',
      wordSpacing: headingSizes.h3?.wordSpacing || '0.01em',
    },
    h4: {
      fontSize: headingSizes.h4?.fontSize || '1.35rem',
      fontWeight: headingSizes.h4?.fontWeight || 800,
      lineHeight: headingSizes.h4?.lineHeight || 1.32,
      letterSpacing: headingSizes.h4?.letterSpacing || '-0.006em',
      wordSpacing: headingSizes.h4?.wordSpacing || '0.01em',
    },
    h5: {
      fontSize: headingSizes.h5?.fontSize || '1.15rem',
      fontWeight: headingSizes.h5?.fontWeight || 800,
      lineHeight: headingSizes.h5?.lineHeight || 1.4,
      letterSpacing: headingSizes.h5?.letterSpacing || '-0.003em',
      wordSpacing: headingSizes.h5?.wordSpacing || '0.008em',
    },
    h6: {
      fontSize: headingSizes.h6?.fontSize || '0.95rem',
      fontWeight: headingSizes.h6?.fontWeight || 800,
      lineHeight: headingSizes.h6?.lineHeight || 1.45,
      letterSpacing: headingSizes.h6?.letterSpacing || '0em',
      wordSpacing: headingSizes.h6?.wordSpacing || '0.006em',
    },
    display: {
      fontFamily: display.fontFamily,
      sizes: { ...displaySizes },
      d1: { ...displaySizes.d1 },
      d2: { ...displaySizes.d2 },
      d3: { ...displaySizes.d3 },
      d4: { ...displaySizes.d4 },
      d5: { ...displaySizes.d5 },
      d6: { ...displaySizes.d6 },
    },
    body1: {
      fontSize: '1.2rem',
      lineHeight: 1.7,
      fontWeight: 500,
      letterSpacing: 0,
      wordSpacing: '0.02em',
    },
    body2: {
      fontSize: '1rem',
      lineHeight: 1.7,
      fontWeight: 500,
      letterSpacing: 0,
      wordSpacing: '0.02em',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.33,
      fontWeight: 500,
      letterSpacing: '0.01em',
      wordSpacing: '0.015em',
    },
  },
  // Canonical breakpoint scale (designTokens/breakpoints.js). Shared with the
  // Mantine theme so both styling systems resolve identical breakpoints.
  breakpoints: {
    values: {
      xs: breakpointPx.xs,
      sm: breakpointPx.sm,
      md: breakpointPx.md,
      lg: breakpointPx.lg,
      xl: breakpointPx.xl,
    },
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          boxShadow: 'none',
        },
        root: {
          zIndex: 1300,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(4, 5),
          borderBottom: 'none',
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(5),
        }),
        dividers: {
          borderTop: 'none',
          borderBottom: 'none',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(4, 5),
          borderTop: 'none',
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
  },
  // Custom design tokens
  customTokens: {
    sizes: other.size,
    colors: {
      ...colors,
      custom: colorsCustom,
    },
  },
});

export default muiTheme;
